import { clearFilingAvailability, storeFilingAvailability } from "./filingAvailability";

/**
 * Utility functions for determining onboarding routes
 * based on user's onboarding status stored in localStorage
 */

/**
 * Get the appropriate route for a logged-in user based on their onboarding status
 * @returns {string} The route path to navigate to
 */
export const getOnboardingRoute = () => {
  const onboardingCompleted = localStorage.getItem("onboardingCompleted");
  const onboardingStep = localStorage.getItem("onboardingStep");
  const discoveryCanProceed = localStorage.getItem("discoveryCanProceed");
  const hasTaxReturnRun = localStorage.getItem("hasTaxReturnRun");

  // If onboarding not complete, go to appropriate step
  if (onboardingCompleted === "false") {
    if (onboardingStep === "2") {
      // If there's a tax return run OR discovery can proceed, go to confirm page
      if (hasTaxReturnRun === "true" || discoveryCanProceed === "true") {
        return "/onboarding/confirm";
      }
      return "/onboarding/questionnaire";
    }
    // Step 1 - start with FY 2024 upload
    return "/onboarding/upload";
  }

  if (onboardingCompleted === "true") {
    return "/onboarding/confirm";
  }

  // Unknown status: go through the tax-app entry route so it can refresh status.
  return "/onboarding";
};

/**
 * Get the appropriate route based on onboarding step and can_proceed status
 * Used when we have the step value directly (e.g., from API response)
 * @param {number} step - The onboarding step (1 or 2)
 * @param {boolean} checkTaxReturnRun - Whether to check for tax_return_run (default true for login redirects)
 * @returns {string} The route path to navigate to
 */
export const getOnboardingStepRoute = (step, checkTaxReturnRun = true) => {
  const discoveryCanProceed = localStorage.getItem("discoveryCanProceed");
  const hasTaxReturnRun = localStorage.getItem("hasTaxReturnRun");

  if (step === 2) {
    // If discovery can proceed, go to confirm page
    if (discoveryCanProceed === "true") {
      return "/onboarding/confirm";
    }
    // If there's a tax return run and we're checking for it (login redirects), go to confirm
    if (checkTaxReturnRun && hasTaxReturnRun === "true") {
      return "/onboarding/confirm";
    }
    return "/onboarding/questionnaire";
  }
  // Step 1 - start with FY 2024 upload
  return "/onboarding/upload";
};

/**
 * Clear all onboarding-related data from localStorage
 * Used on logout or invalid token
 */
export const clearOnboardingData = () => {
  clearFilingAvailability();
  localStorage.removeItem("onboardingCompleted");
  localStorage.removeItem("onboardingStep");
  localStorage.removeItem("discoveryCanProceed");
  localStorage.removeItem("hasTaxReturnRun");
  localStorage.removeItem("onboardingFileCount");
  localStorage.removeItem("efileStatus");
  localStorage.removeItem("isWhitelistedUser");
  localStorage.removeItem("docusignLastSigningRole");
  localStorage.removeItem("docusignMaxRetriesHit");
  localStorage.removeItem("isSpouseSession");
  localStorage.removeItem("postLoginRedirect");
};

const INVESTING_COMPLETED_KEY = "investingOnboardingCompleted";
const INVESTING_STEP_KEY = "investingOnboardingStep";
const INVESTING_UNLOCKED_KEY = "investingUnlocked";

export const clearInvestingLocalState = () => {
  localStorage.removeItem("investingUserMarkedNo2025FiledReturn");
  localStorage.removeItem("investingUserMarked2025DocsIncomplete");
  localStorage.removeItem("investingUserMarkedNo2026Docs");
  localStorage.removeItem(INVESTING_COMPLETED_KEY);
  localStorage.removeItem(INVESTING_STEP_KEY);
  localStorage.removeItem(INVESTING_UNLOCKED_KEY);
  localStorage.removeItem("investingDashboardTab");
  localStorage.removeItem("investingPlaidLinkToken");
  localStorage.removeItem("investingWelcomeSeen");
  localStorage.removeItem("investingHadPreview");
  localStorage.removeItem("investingConfirmedSeen");
  localStorage.removeItem("postLoginRedirect");
};

export const resolveInvestingRoute = (completed, step) => {
  if (completed === true) {
    return "/investing/dashboard";
  }
  const s = Number(step);
  if (s >= 3) {
    return "/investing/questionnaire";
  }
  if (s === 2) {
    return "/investing/upload-2026";
  }
  return "/investing/upload";
};

// Where /investing should land. A user with a servable return (a preview built
// from their 2025 filing, or a confirmed one) lives on the dashboard; the step
// screens are reached from the dashboard's setup strip rather than being the
// front door.
export const resolveInvestingEntryRoute = (completed, step, unlocked) => {
  if (unlocked === true) {
    return "/investing/dashboard";
  }
  return resolveInvestingRoute(completed, step);
};

export const storeInvestingOnboardingData = (data) => {
  if (typeof data?.onboarding_completed === "boolean") {
    localStorage.setItem(INVESTING_COMPLETED_KEY, String(data.onboarding_completed));
  }
  if (data?.onboarding_step !== undefined && data?.onboarding_step !== null) {
    localStorage.setItem(INVESTING_STEP_KEY, String(data.onboarding_step));
  }
  if (typeof data?.unlocked === "boolean") {
    localStorage.setItem(INVESTING_UNLOCKED_KEY, String(data.unlocked));
  }
};

export const isInvestingUnlockedFromCache = () =>
  localStorage.getItem(INVESTING_UNLOCKED_KEY) === "true";

export const getInvestingRouteFromCache = () => {
  const step = localStorage.getItem(INVESTING_STEP_KEY);
  if (step === null) {
    return null;
  }
  const completed = localStorage.getItem(INVESTING_COMPLETED_KEY) === "true";
  return resolveInvestingEntryRoute(completed, step, isInvestingUnlockedFromCache());
};

export const syncInvestingFlag = (value, storageKey, setState) => {
  if (typeof value !== "boolean") {
    return;
  }
  setState(value);
  if (value) {
    localStorage.setItem(storageKey, "true");
  } else {
    localStorage.removeItem(storageKey);
  }
};

export const investingAccessRedirect = (data, requiredStep) => {
  storeInvestingOnboardingData(data);
  if (data?.onboarding_completed === true) {
    return "/investing/dashboard";
  }
  // The two 2025 screens (the only ones with requiredStep 1) are not shown at
  // all to taxpayers whose 2025 filing already lives with us.
  if (data?.skip_2025_onboarding === true && requiredStep < 2) {
    return resolveInvestingRoute(false, data?.onboarding_step);
  }
  const step = Number(data?.onboarding_step);
  if (Number.isFinite(step) && step < requiredStep) {
    return resolveInvestingRoute(false, step);
  }
  return null;
};

/**
 * Store or clear cached e-file status for cross-page checks
 * @param {string|null|undefined} status - efile_data.efile_status from backend
 */
export const setEfileStatus = (status) => {
  if (typeof status === "string" && status.trim()) {
    localStorage.setItem("efileStatus", status);
    return;
  }
  localStorage.removeItem("efileStatus");
};

/**
 * E-file statuses that block data modification (delete, reset, etc.)
 */
const BLOCKED_EFILE_STATUSES = new Set([
  'efile_successful',
  'efile_attempted',
  'efile_rejection',
  'efile_rejection_closed',
  '8879_successful',
]);

/**
 * Check if the user's return is in a state that blocks data modification
 * @returns {boolean} True if efile_status blocks modifications
 */
export const isEfileSuccessful = () => {
  const status = localStorage.getItem("efileStatus");
  return BLOCKED_EFILE_STATUSES.has(status);
};

export const isDocusignMaxRetriesHit = () => {
  return localStorage.getItem("docusignMaxRetriesHit") === "true";
};

/**
 * Returns the reason data deletion is locked, or null if not locked.
 * Priority: max_retries > efile_successful > other blocked statuses (processing)
 */
export const getDeleteLockReason = () => {
  const status = localStorage.getItem("efileStatus");
  if (status === 'efile_successful') return 'efile';
  if (isDocusignMaxRetriesHit()) return 'max_retries';
  if (BLOCKED_EFILE_STATUSES.has(status)) return 'processing';
  return null;
};

/**
 * Check if a given status is a blocked e-file status
 * @param {string} status - The efile_status to check
 * @returns {boolean} True if the status blocks modifications
 */
export const isBlockedEfileStatus = (status) => {
  return BLOCKED_EFILE_STATUSES.has(status);
};

/**
 * Cache only the onboarding step, for when the server confirms a new step without a full dashboard payload
 * (storeOnboardingData would overwrite every other cached field from a partial payload).
 * @param {number} step - The onboarding step the server confirmed
 */
export const storeOnboardingStep = (step) => {
  if (step !== undefined && step !== null) {
    localStorage.setItem("onboardingStep", String(step));
  }
};

/**
 * Store onboarding data in localStorage
 * Used to cache onboarding status for instant routing decisions
 * @param {Object} data - Dashboard API response data
 * @param {boolean} [data.onboarding_completed] - Whether onboarding is complete
 * @param {number} [data.onboarding_step] - Current onboarding step (1 or 2)
 * @param {Object} [data.onboarding_data] - Onboarding data object
 * @param {boolean} [data.onboarding_data.can_proceed_from_discovery] - Whether user can proceed from discovery
 * @param {Object} [data.tax_return_run] - Tax return run object (if exists)
 * @param {Object} [data.efile_data] - E-file data object (if exists)
 */
export const storeOnboardingData = (data) => {
  storeFilingAvailability(data);
  // The investing allowlist is edited server-side and can change at any time,
  // and this flag is only what surfaces the switch-to-investing entry point
  // (the investing app itself is reachable by URL either way). The dashboard
  // payload carries the current answer and is fetched on every app load
  // (App.jsx prefetch), so refreshing is enough to pick up a new entry.
  // Guarded on the field being present so a payload without it never clobbers
  // the cache.
  if (typeof data?.is_whitelisted_user === "boolean") {
    localStorage.setItem("isWhitelistedUser", String(data.is_whitelisted_user));
  }
  if (data?.onboarding_completed !== undefined) {
    localStorage.setItem("onboardingCompleted", String(data.onboarding_completed));
  }
  if (data?.onboarding_step !== undefined) {
    localStorage.setItem("onboardingStep", String(data.onboarding_step));
  }
  if (data?.onboarding_data?.can_proceed_from_discovery !== undefined) {
    localStorage.setItem("discoveryCanProceed", String(data.onboarding_data.can_proceed_from_discovery));
  }
  // Store whether there's a tax_return_run (used for routing to confirm page)
  const hasTaxReturnRun = data?.tax_return_run && Object.keys(data.tax_return_run).length > 0;
  localStorage.setItem("hasTaxReturnRun", String(!!hasTaxReturnRun));
  // Store (or clear) e-file status (backend may use efile_status or status)
  setEfileStatus(data?.efile_data?.efile_status ?? data?.efile_data?.status);
  // Store DocuSign max KBA retries hit flag
  if (data?.efile_data?.docusign_data?.max_kba_retries_hit) {
    localStorage.setItem("docusignMaxRetriesHit", "true");
  } else {
    localStorage.removeItem("docusignMaxRetriesHit");
  }
};
